export const BOARD_FOOTAGE_FIELD = 'approximate_board_footage';

export const SLAB_DIMENSION_FIELD_KEYS = {
  widest: 'Widest_Measurement',
  narrowest: 'Narrowest_Measurement',
  length: 'Length',
  thickness: 'thickness',
};

const PUBLIC_DATA_PREFIX = 'pub_';
const BOARD_FOOTAGE_PRECISION = 100;

const dimensionFieldDefinitions = {
  widest: {
    keys: [
      SLAB_DIMENSION_FIELD_KEYS.widest,
      'Largest_Measurement',
      'Largest_Width',
      'largest_width',
      'largestWidth',
      'widest_width',
    ],
    labels: ['Largest width', 'Widest measurement', 'Widest width', 'Maximum width', 'Max width'],
  },
  narrowest: {
    keys: [
      SLAB_DIMENSION_FIELD_KEYS.narrowest,
      'Smallest_Measurement',
      'Smallest_Width',
      'smallest_width',
      'smallestWidth',
      'narrowest_width',
    ],
    labels: [
      'Smallest width',
      'Narrowest measurement',
      'Narrowest width',
      'Minimum width',
      'Min width',
    ],
  },
  length: {
    keys: [SLAB_DIMENSION_FIELD_KEYS.length, 'length', 'slab_length'],
    labels: ['Length', 'Slab length'],
  },
  thickness: {
    keys: [SLAB_DIMENSION_FIELD_KEYS.thickness, 'Thickness', 'slab_thickness'],
    labels: ['Thickness'],
  },
};

const wordNumbers = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  half: 0.5,
};

const hasValue = value => value !== null && typeof value !== 'undefined' && value !== '';

const roundBoardFootage = value =>
  Math.round((value + Number.EPSILON) * BOARD_FOOTAGE_PRECISION) / BOARD_FOOTAGE_PRECISION;

const normalizeFieldIdentifier = value =>
  `${value || ''}`
    .toLowerCase()
    .replace(/^pub_/, '')
    .replace(/^priv_/, '')
    .replace(/[^a-z0-9]/g, '');

const parseWordMeasurement = value => {
  const normalized = value
    .replace(/[_-]/g, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = normalized.split(' ').filter(Boolean);

  if (words.length === 0) {
    return null;
  }

  const quarterIndex = words.findIndex(word => word === 'quarter' || word === 'quarters');
  if (quarterIndex > 0) {
    const previousWordNumber = wordNumbers[words[quarterIndex - 1]];
    return Number.isFinite(previousWordNumber) ? previousWordNumber / 4 : null;
  }

  const total = words.reduce((sum, word) => {
    const value = wordNumbers[word];
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  return total > 0 ? total : null;
};

export const parseMeasurementValue = (value, fieldConfig) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (!hasValue(value)) {
    return null;
  }

  const selectedOption = fieldConfig?.enumOptions?.find(
    option => `${option.option}` === `${value}` || `${option.label}` === `${value}`
  );
  const candidates = [selectedOption?.label, selectedOption?.option, value].filter(hasValue);

  for (const candidate of candidates) {
    const normalized = `${candidate}`
      .toLowerCase()
      .replace(/[“”"″]/g, '')
      .replace(/\b(inches|inch|in\.)\b/g, '')
      .replace(/(\d)\s*-\s*(\d+\s*\/\s*\d+)/g, '$1 $2')
      .trim();

    const mixedFractionMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s+(\d+)\s*\/\s*(\d+)/);
    if (mixedFractionMatch) {
      const whole = Number.parseFloat(mixedFractionMatch[1]);
      const numerator = Number.parseInt(mixedFractionMatch[2], 10);
      const denominator = Number.parseInt(mixedFractionMatch[3], 10);
      if (denominator !== 0) {
        return whole + numerator / denominator;
      }
    }

    const fractionMatch = normalized.match(/(-?\d+)\s*\/\s*(\d+)/);
    if (fractionMatch) {
      const numerator = Number.parseInt(fractionMatch[1], 10);
      const denominator = Number.parseInt(fractionMatch[2], 10);
      if (denominator !== 0) {
        return numerator / denominator;
      }
    }

    const decimalMatch = normalized.match(/-?\d+(?:\.\d+)?/);
    if (decimalMatch) {
      return Number.parseFloat(decimalMatch[0]);
    }

    const wordMeasurement = parseWordMeasurement(normalized);
    if (Number.isFinite(wordMeasurement)) {
      return wordMeasurement;
    }
  }

  return null;
};

const getFieldLabel = fieldConfig =>
  fieldConfig?.saveConfig?.label || fieldConfig?.showConfig?.label || fieldConfig?.label;

const getFieldConfig = (listingFieldsConfig = [], definition) => {
  const normalizedKeys = definition.keys.map(normalizeFieldIdentifier);
  const normalizedLabels = definition.labels.map(normalizeFieldIdentifier);

  return listingFieldsConfig.find(fieldConfig => {
    const normalizedKey = normalizeFieldIdentifier(fieldConfig?.key);
    const normalizedLabel = normalizeFieldIdentifier(getFieldLabel(fieldConfig));
    return normalizedKeys.includes(normalizedKey) || normalizedLabels.includes(normalizedLabel);
  });
};

const getDimensionValue = (values = {}, definition, fieldConfig) => {
  const candidateKeys = [fieldConfig?.key, ...definition.keys].filter(hasValue);
  for (const key of candidateKeys) {
    const prefixedKey = `${PUBLIC_DATA_PREFIX}${key}`;
    if (hasValue(values[prefixedKey])) {
      return values[prefixedKey];
    }
    if (hasValue(values[key])) {
      return values[key];
    }
  }

  const normalizedKeys = definition.keys.map(normalizeFieldIdentifier);
  const matchingEntry = Object.entries(values).find(([key, value]) => {
    const normalizedKey = normalizeFieldIdentifier(key);
    return normalizedKeys.includes(normalizedKey) && hasValue(value);
  });
  return matchingEntry ? matchingEntry[1] : null;
};

export const calculateApproximateBoardFootage = (values = {}, listingFieldsConfig = []) => {
  const widestField = getFieldConfig(listingFieldsConfig, dimensionFieldDefinitions.widest);
  const narrowestField = getFieldConfig(listingFieldsConfig, dimensionFieldDefinitions.narrowest);
  const lengthField = getFieldConfig(listingFieldsConfig, dimensionFieldDefinitions.length);
  const thicknessField = getFieldConfig(listingFieldsConfig, dimensionFieldDefinitions.thickness);
  const widest = parseMeasurementValue(
    getDimensionValue(values, dimensionFieldDefinitions.widest, widestField),
    widestField
  );
  const narrowest = parseMeasurementValue(
    getDimensionValue(values, dimensionFieldDefinitions.narrowest, narrowestField),
    narrowestField
  );
  const length = parseMeasurementValue(
    getDimensionValue(values, dimensionFieldDefinitions.length, lengthField),
    lengthField
  );
  const thickness = parseMeasurementValue(
    getDimensionValue(values, dimensionFieldDefinitions.thickness, thicknessField),
    thicknessField
  );
  const measurements = [widest, narrowest, length, thickness];
  const hasValidMeasurements = measurements.every(value => Number.isFinite(value) && value > 0);

  if (!hasValidMeasurements) {
    return null;
  }

  return roundBoardFootage((((widest + narrowest) / 2) * length * thickness) / 144);
};

export const formatBoardFootage = value => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
};
